import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { notify } from '@staff/ui'

import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { ApiRequestError } from '@/services/application'
import { createInitialFormState, useApplicationFormData, type CreateApplicationFormState } from './applicationFormData'

type EditApplicationDraftMessages = {
  loadFailed: string
  saveFailed: string
  draftSaved: (id: string) => string
}

type EditApplicationDraftOptions<TDetail> = {
  fetchForm: (id: string) => Promise<TDetail>
  /** Fills the shared form from this flow's own read model. */
  hydrate: (form: CreateApplicationFormState, detail: TDetail) => void
  /** Builds the payload and PUTs it. Kept in the caller so mappers stay out of here. */
  update: (id: string) => Promise<void>
  submit: (id: string) => Promise<void>
  /** Where to land after a successful submit. */
  redirectTo: (id: string) => string
  messages: EditApplicationDraftMessages
}

/**
 * Owns the edit lifecycle of a single application draft - loading it into the shared
 * form, saving it, submitting it, and the loading/error/success state all three share.
 *
 * Application and commitment drafts differ only in which endpoints they call and what
 * they call themselves, so both flows drive this with their own options.
 */
export const useEditApplicationDraft = <TDetail>(options: EditApplicationDraftOptions<TDetail>) => {
  const route = useRoute()
  const router = useRouter()
  const { loadPersonnelDirectory } = usePersonnelDirectory()
  const { form, canSaveDraft } = useApplicationFormData()

  const applicationId = computed(() => route.params.id?.toString() ?? '')

  // Starts true so the first render shows the loading state rather than building the
  // form from the module-global `form`, which may still hold the previous view's values.
  const isLoadingApplication = ref(true)
  const hasLoadedApplication = ref(false)
  const isSubmitting = ref(false)
  const submitAction = ref<'draft' | 'submit' | null>(null)
  const error = ref('')
  const errorDetails = ref<string[]>([])
  const errorDebug = ref('')
  const success = ref('')

  const clearFeedback = () => {
    error.value = ''
    errorDetails.value = []
    errorDebug.value = ''
    success.value = ''
  }

  const loadApplication = async (id: string) => {
    isLoadingApplication.value = true
    hasLoadedApplication.value = false
    error.value = ''

    try {
      const application = await options.fetchForm(id)

      options.hydrate(form, application)
      hasLoadedApplication.value = true
      return true
    } catch (err) {
      Object.assign(form, createInitialFormState())
      error.value = err instanceof Error ? err.message : options.messages.loadFailed
      return false
    } finally {
      isLoadingApplication.value = false
    }
  }

  const persist = async (mode: 'draft' | 'submit') => {
    if (isSubmitting.value || !hasLoadedApplication.value) return

    submitAction.value = mode
    clearFeedback()

    if (mode === 'draft' && !canSaveDraft.value) {
      const description = 'Fyll i personnummer för alla låntagare innan du sparar utkast.'
      error.value = description
      notify({ type: 'error', title: 'Personnummer saknas', description })
      submitAction.value = null
      return
    }

    isSubmitting.value = true

    try {
      const id = applicationId.value
      await options.update(id)
      form.deletedApplicantPersonalNumbers.splice(0)

      if (mode === 'submit') {
        await options.submit(id)
        await router.push(options.redirectTo(id))
      } else if (await loadApplication(id)) {
        success.value = `Utkast sparat. ID: ${id}`
        notify({ type: 'success', title: 'Utkast sparat', description: options.messages.draftSaved(id) })
      } else {
        // The update went through - only the reload failed, and that blanks the form.
        // Saying just "sparat" next to an empty form and an error box would be misleading.
        notify({
          type: 'warning',
          title: 'Utkast sparat, men kunde inte läsas in igen',
          description: 'Ändringarna är sparade. Ladda om sidan för att fortsätta redigera.',
          dismissable: true,
        })
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        error.value = `${err.message} (HTTP ${err.status})`
        errorDetails.value = err.details
        errorDebug.value = err.debug ?? ''
      } else {
        error.value = err instanceof Error ? err.message : options.messages.saveFailed
      }
    } finally {
      isSubmitting.value = false
      submitAction.value = null
    }
  }

  const loadCurrentDraft = async () => {
    await Promise.all([loadPersonnelDirectory(), loadApplication(applicationId.value)])
  }

  watch(applicationId, () => {
    void loadCurrentDraft()
  })

  onMounted(() => {
    void loadCurrentDraft()
  })

  return {
    isLoadingApplication,
    hasLoadedApplication,
    isSubmitting,
    submitAction,
    error,
    errorDetails,
    errorDebug,
    success,
    canSaveDraft,
    onUpdateDraft: () => persist('draft'),
    onSubmit: () => persist('submit'),
  }
}
